import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Input, Modal, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import {
  FIELD_LIMITS,
  PAYOUT_STATUS_COLORS,
  PAYOUT_STATUS_OPTIONS,
  EMPTY_STATES,
  getStatusColor,
} from '../constants';
import { formatCurrency, formatDate } from '../utils/formatters';
import { toApiDateRange } from '../utils/dateRange';
import {
  PAYOUT_ACTION_LABELS,
  allowedTransitions,
  requiresReference,
} from '../utils/payoutStatus';
import { FilterGroup, FilterLabel, FilterRow, TableActions } from '../Styles';
import CreatorProgramTable from '../components/CreatorProgramTable';
import FilterSelect from '../components/FilterSelect';
import ListPagination from '../components/ListPagination';
import QueryErrorState from '../components/QueryErrorState';
import { usePaginationState } from '../hooks/usePaginationState';
import { useCreatorOptions } from '../hooks/useSelectOptions';
import { usePayouts } from '../hooks/useCreatorQueries';
import { useExportPayouts, useUpdatePayoutStatus } from '../hooks/useCreatorMutations';
import DraftPayoutModal from '../DraftPayoutModal';
import PayoutDetailModal from '../PayoutDetailModal';
import type { Payout, PayoutStatus } from '../types';
import type { DateRange } from '../../../commonComponents/DateRangePicker/types';

const { Text } = Typography;

/**
 * §1.7 — payouts.
 *
 * Row actions are generated from `PAYOUT_TRANSITIONS`, which encodes the §5.4 state
 * machine as data. A terminal payout (`paid`/`cancelled`) therefore renders no
 * actions at all, and an illegal jump like `draft → paid` can't be offered by
 * construction rather than by remembering to guard it here.
 *
 * There is no "edit amount" action: the amount is derived from the swept rows, never
 * entered.
 */
const PayoutsTab: React.FC = () => {
  const [referenceForm] = Form.useForm<{ reference: string }>();

  const { page, pageSize, onPaginationChange, withPageReset } = usePaginationState();
  const [creatorId, setCreatorId] = useState<string | undefined>();
  const [status, setStatus] = useState<PayoutStatus | undefined>();
  const [isDraftOpen, setIsDraftOpen] = useState(false);
  /**
   * Empty by default so the export still covers everything until a range is picked —
   * the old behaviour. `useDateRangeFilter` is deliberately not used here: its
   * trailing-30-day seed would silently narrow every export.
   */
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [detailPayout, setDetailPayout] = useState<Payout | null>(null);
  /** Set while collecting the `reference` required to mark a payout paid. */
  const [payingPayout, setPayingPayout] = useState<Payout | null>(null);

  const { options: creatorOptions, loading: creatorsLoading } = useCreatorOptions();

  const params = useMemo(
    () => ({ creator: creatorId, status, page, limit: pageSize }),
    [creatorId, status, page, pageSize]
  );

  const { data, isLoading, isFetching, isError, refetch } = usePayouts(params);
  const { mutate: updateStatus, isPending: isUpdating } = useUpdatePayoutStatus();
  const { mutate: exportPayouts, isPending: isExporting } = useExportPayouts();

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleCreatorChange = useMemo(
    () => withPageReset<string | undefined>(setCreatorId),
    [withPageReset]
  );
  const handleStatusChange = useMemo(
    () => withPageReset<PayoutStatus | undefined>(setStatus),
    [withPageReset]
  );

  /**
   * The range scopes the CSV only.
   *
   * `GET /merchant/payouts` (§5.2) whitelists its query params and answers
   * `400 property startDate should not exist` if either is sent, so the list cannot
   * be date-filtered server-side and nothing here goes into `params` above. The
   * label says "export" for exactly that reason — see the note beside the picker.
   */
  const exportRange = useMemo(() => toApiDateRange(dateRange), [dateRange]);

  const handleTransition = useCallback((payout: Payout, next: PayoutStatus) => {
    // `paid` is the only transition that needs extra input, so it routes through a
    // modal; everything else is a confirm.
    if (requiresReference(next)) {
      referenceForm.resetFields();
      setPayingPayout(payout);
      return;
    }

    Modal.confirm({
      title: `${PAYOUT_ACTION_LABELS[next]} payout?`,
      content:
        next === 'cancelled'
          ? 'Cancelling releases every swept row back to unassigned, so they become eligible for the next payout drafted for this creator.'
          : 'Approving is bookkeeping only — no transfer is triggered by this action.',
      okText: PAYOUT_ACTION_LABELS[next],
      okType: next === 'cancelled' ? 'danger' : 'primary',
      onOk: () => updateStatus({ id: payout.id, payload: { status: next } }),
    });
  }, [referenceForm, updateStatus]);

  const handleMarkPaid = (values: { reference: string }) => {
    if (!payingPayout) return;
    updateStatus(
      { id: payingPayout.id, payload: { status: 'paid', reference: values.reference.trim() } },
      { onSuccess: () => setPayingPayout(null) }
    );
  };

  const columns: ColumnsType<Payout> = useMemo(
    () => [
      {
        title: 'CREATOR',
        key: 'creator',
        fixed: 'left',
        width: 190,
        render: (_, record) => record.creator?.name || record.creatorId,
      },
      {
        title: 'AMOUNT',
        key: 'amount',
        width: 130,
        render: (_, record) => <strong>{formatCurrency(record.amount)}</strong>,
      },
      {
        title: 'STATUS',
        key: 'status',
        width: 120,
        render: (_, record) => (
          <Tag color={getStatusColor(record.status, PAYOUT_STATUS_COLORS)}>{record.status}</Tag>
        ),
      },
      {
        title: 'PERIOD',
        key: 'period',
        width: 210,
        render: (_, record) =>
          `${formatDate(record.periodStart)} → ${formatDate(record.periodEnd)}`,
      },
      {
        title: 'REFERENCE',
        key: 'reference',
        width: 160,
        render: (_, record) =>
          record.reference ? (
            <Text ellipsis style={{ maxWidth: 140 }}>
              {record.reference}
            </Text>
          ) : (
            '—'
          ),
      },
      {
        title: 'PAID AT',
        key: 'paidAt',
        width: 130,
        render: (_, record) => formatDate(record.paidAt),
      },
      {
        title: 'ACTIONS',
        key: 'actions',
        width: 150,
        render: (_, record) => {
          const allowed = allowedTransitions(record.status);

          return (
            <TableActions>
              <Button size="small" onClick={() => setDetailPayout(record)}>
                View
              </Button>

              {/* Terminal payouts get no action menu at all. */}
              {allowed.length > 0 && (
                <Dropdown
                  disabled={isUpdating}
                  menu={{
                    items: allowed.map((next) => ({
                      key: next,
                      label: PAYOUT_ACTION_LABELS[next],
                      danger: next === 'cancelled',
                      onClick: () => handleTransition(record, next),
                    })),
                  }}
                  trigger={['click', 'hover']}
                >
                  <Button size="small" icon={<MoreOutlined />} />
                </Dropdown>
              )}
            </TableActions>
          );
        },
      },
    ],
    [isUpdating, handleTransition]
  );

  if (isError && rows.length === 0) {
    return (
      <QueryErrorState title="Could not load payouts" onRetry={() => refetch()} />
    );
  }

  return (
    <>
      <FilterRow style={{ marginBottom: 16, justifyContent: 'space-between' }}>
        <Space wrap align="end">
          <FilterSelect
            label="Creator"
            placeholder="All creators"
            options={creatorOptions}
            loading={creatorsLoading}
            value={creatorId}
            onChange={handleCreatorChange}
            minWidth={220}
          />

          <FilterSelect<PayoutStatus>
            label="Status"
            placeholder="All statuses"
            options={PAYOUT_STATUS_OPTIONS}
            value={status}
            onChange={handleStatusChange}
            searchable={false}
            minWidth={160}
          />

          <FilterGroup>
            <FilterLabel>Date range (export only)</FilterLabel>
            <DateRangePicker
              selectedDateRange={dateRange}
              onDateRangeChange={setDateRange}
              placeholder="All dates"
            />
          </FilterGroup>
        </Space>

        <Space wrap>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'summary',
                  label: 'One row per payout',
                  onClick: () =>
                    exportPayouts({ creator: creatorId, status, ...exportRange }),
                },
                {
                  key: 'detail',
                  label: 'One row per attribution',
                  onClick: () =>
                    exportPayouts({ creator: creatorId, status, ...exportRange, detail: 1 }),
                },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />} loading={isExporting}>
              Export CSV
            </Button>
          </Dropdown>

          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsDraftOpen(true)}>
            Draft Payout
          </Button>
        </Space>
      </FilterRow>

      <CreatorProgramTable<Payout>
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={isLoading || isFetching}
        emptyText={EMPTY_STATES.PAYOUTS}
      />

      <ListPagination
        visible={rows.length > 0}
        totalCount={total}
        currentPage={page}
        pageSize={pageSize}
        onChange={onPaginationChange}
        totalLabel="Total payouts"
      />

      <DraftPayoutModal open={isDraftOpen} onClose={() => setIsDraftOpen(false)} />

      <PayoutDetailModal
        open={!!detailPayout}
        payout={detailPayout}
        onClose={() => setDetailPayout(null)}
      />

      <Modal
        title="Mark payout as paid"
        open={!!payingPayout}
        onCancel={() => setPayingPayout(null)}
        onOk={() => referenceForm.submit()}
        okText="Mark as paid"
        confirmLoading={isUpdating}
        destroyOnHidden
      >
        <Form form={referenceForm} layout="vertical" onFinish={handleMarkPaid}>
          <Form.Item
            label="Payment reference"
            name="reference"
            extra="Records how you actually paid them — a wire or Venmo confirmation number. This API never moves money itself."
            rules={[
              { required: true, message: 'A reference is required to mark a payout paid' },
              {
                max: FIELD_LIMITS.REFERENCE_MAX,
                message: `At most ${FIELD_LIMITS.REFERENCE_MAX} characters`,
              },
            ]}
          >
            <Input placeholder="e.g. WIRE-2026-00184" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PayoutsTab;
